// Admin "Ads & Funnel" tab — are Meta/TikTok receiving signup + purchase
// conversions, how Get Clips clicks become signups and subscribers, where
// clicks come from, and a PostHog summary. Data: GET /admin/analytics/funnel.
import { useEffect, useState } from "react";
import { axiosInstance } from "../../config";
import toast from "react-hot-toast";

const RANGES = [7, 30, 90];
const PLATFORM_LABEL = { meta: "Meta", tiktok: "TikTok" };
const EVENT_LABEL = {
  CompleteRegistration: "Signup (CompleteRegistration)",
  InitiateCheckout: "Checkout started (InitiateCheckout)",
  StartTrial: "Trial start (StartTrial)",
  Purchase: "Subscriber (Purchase)",
  CompletePayment: "Subscriber (CompletePayment)",
};

function rel(d) {
  if (!d) return "never";
  const s = Math.floor((Date.now() - new Date(d).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60); if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function Stat({ label, value, sub }) {
  return (
    <div className="admindash-stat">
      <div className="admindash-stat-value">{value}</div>
      <div className="admindash-stat-label">{label}</div>
      {sub && <div className="funnel-stat-sub">{sub}</div>}
    </div>
  );
}

// Health of one platform × event row.
function health(row, configured) {
  if (!configured) return { label: "Not configured", tone: "bad" };
  if (row.failed > 0 && row.sent === 0) return { label: "Failing", tone: "bad" };
  if (row.failed > 0) return { label: "Some failures", tone: "warn" };
  if (row.sent > 0) return { label: "Working", tone: "good" };
  return { label: "No events yet", tone: "idle" };
}

function Bars({ title, data, orange }) {
  // Long ranges show the last 30 days as bars; the total covers the full range.
  const shown = data.slice(-30);
  const max = Math.max(1, ...shown.map((d) => d.count));
  const total = data.reduce((s, d) => s + d.count, 0);
  const last7 = data.slice(-7).reduce((s, d) => s + d.count, 0);
  return (
    <div className="funnel-mini">
      <div className="funnel-mini-title">
        {title}
        <span className="funnel-mini-counts"><span className="funnel-mini-7d">7 days: <b>{last7}</b></span> <b>{total}</b></span>
      </div>
      <div className="funnel-bars">
        {shown.map((d, i) => (
          <div key={d.date} className="funnel-bar-col" title={`${d.date}: ${d.count}`}>
            <div className="funnel-bar-area">
              <div
                className={`funnel-bar${orange ? " funnel-bar--orange" : ""}${d.count ? "" : " funnel-bar--zero"}`}
                style={{ height: d.count ? `${Math.max(6, (d.count / max) * 100)}%` : undefined }}
              />
            </div>
            <span className="funnel-bar-label">{(shown.length - 1 - i) % 5 === 0 ? d.date.slice(5) : ""}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function TestEvent({ configured }) {
  const [platform, setPlatform] = useState("meta");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      const r = await axiosInstance.post("/admin/analytics/funnel/test-event", { platform, testEventCode: code });
      if (r.data.ok) toast.success(`Sent — check ${PLATFORM_LABEL[platform]} Events Manager → Test Events`);
      else toast.error(r.data.message || r.data.result?.error || "Send failed");
    } catch (e) {
      toast.error(e.response?.data?.error || "Send failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="funnel-test">
      <div className="muted" style={{ fontSize: 13 }}>
        Verify the connection: open {platform === "meta" ? "Meta Events Manager → your dataset" : "TikTok Events Manager → your pixel"} → <b>Test Events</b>,
        copy the test code, paste it here and send. It shows up there within seconds and is not counted as real data.
      </div>
      <div className="funnel-test-row">
        <select value={platform} onChange={(e) => setPlatform(e.target.value)} className="funnel-input">
          <option value="meta">Meta{configured.meta ? "" : " (not configured)"}</option>
          <option value="tiktok">TikTok{configured.tiktok ? "" : " (not configured)"}</option>
        </select>
        <input className="funnel-input" placeholder="Test event code" value={code} onChange={(e) => setCode(e.target.value)} />
        <button className="funnel-btn" onClick={send} disabled={busy || !code.trim()}>
          {busy ? "Sending…" : "Send test signup"}
        </button>
      </div>
    </div>
  );
}

function PostHogPanel({ ph }) {
  if (!ph) return null;
  if (!ph.configured) {
    return (
      <div className="admindash-section admindash-fade-in">
        <h2>PostHog</h2>
        <div className="admindash-empty" style={{ textAlign: "left" }}>
          PostHog is collecting data on clipbait.ai and the app but isn't connected to this dashboard yet. On the API service in Render, set
          <b> POSTHOG_PERSONAL_API_KEY</b> (PostHog → Settings → Personal API keys, scope "Query: read"), then redeploy.
        </div>
      </div>
    );
  }
  if (ph.error) {
    return (
      <div className="admindash-section admindash-fade-in">
        <h2>PostHog</h2>
        <div className="admindash-empty">Couldn't load PostHog: {ph.error}</div>
      </div>
    );
  }
  return (
    <div className="admindash-section admindash-fade-in">
      <h2>
        PostHog{" "}
        <a className="muted funnel-link" href={ph.dashboardUrl} target="_blank" rel="noopener noreferrer">open PostHog →</a>
      </h2>
      <div className="admindash-grid funnel-grid">
        <Stat label="Visitors" value={ph.visitors.toLocaleString()} />
        <Stat label="Pageviews" value={ph.pageviews.toLocaleString()} />
        <Stat label="Get Clips clicks" value={ph.getClipsClicks.toLocaleString()} sub={`${ph.getClipsClickers} people`} />
      </div>
      <div className="funnel-two-col">
        <table className="admindash-table">
          <thead><tr><th>Country</th><th>Visitors</th></tr></thead>
          <tbody>{ph.countries.map((c) => <tr key={c.country}><td>{c.country}</td><td>{c.visitors}</td></tr>)}</tbody>
        </table>
        <table className="admindash-table">
          <thead><tr><th>Page</th><th>Views</th></tr></thead>
          <tbody>{ph.pages.map((p) => <tr key={p.page}><td className="funnel-ellipsis">{p.page}</td><td>{p.views}</td></tr>)}</tbody>
        </table>
      </div>
    </div>
  );
}

const SOURCE_LABEL = { meta: "Meta", tiktok: "TikTok", google: "Google", youtube: "YouTube", organic: "Organic" };
// Projected revenue: running trials that aren't cancelled, by charge day.
function TrialPipeline({ p }) {
  const [open, setOpen] = useState(false);
  const dayKey = (d) => new Date(d).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
  const byDay = [];
  for (const u of p.upcoming) {
    const k = dayKey(u.chargeAt);
    const last = byDay[byDay.length - 1];
    if (last?.day === k) { last.count++; last.cents += u.cents; } else byDay.push({ day: k, count: 1, cents: u.cents });
  }
  const usd = (c) => money(c / 100);
  return (
    <div className="admindash-section">
      <h2 className="admindash-section-title">Projected revenue from active trials</h2>
      <div className="admindash-grid funnel-grid">
        <Stat label="Projected first charges" value={usd(p.projectedCents)}
          sub={`${p.count} trial${p.count === 1 ? "" : "s"} still on, not cancelled`} />
        {byDay.slice(0, 3).map((d) => (
          <Stat key={d.day} label={d.day} value={usd(d.cents)} sub={`${d.count} trial${d.count === 1 ? "" : "s"} end`} />
        ))}
        <Stat label="Cancelled trials" value={p.canceled} sub={`${usd(p.canceledCents)} they won't pay`} />
      </div>
      {p.upcoming.length > 0 && (
        <>
          <button type="button" className="funnel-pipeline-toggle" onClick={() => setOpen((v) => !v)}>
            {open ? "Hide" : "Show"} each trial
          </button>
          {open && (
            <table className="admindash-table">
              <thead><tr><th>Charges on</th><th>Amount</th><th>Customer</th></tr></thead>
              <tbody>
                {p.upcoming.map((u, i) => (
                  <tr key={i}>
                    <td>{new Date(u.chargeAt).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</td>
                    <td>{usd(u.cents)}{u.coupon ? <span className="muted"> · {u.coupon}</span> : null}</td>
                    <td>{u.email || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
      <div className="admindash-note">
        What Stripe will try to charge when each trial ends (first month after any regional discount), if every card goes
        through. Cancelled trials charge nothing. Updates every 5 minutes.
      </div>
    </div>
  );
}

const money = (v, cents) => (v == null ? "—" : `$${v.toLocaleString(undefined, { minimumFractionDigits: cents ? 2 : 0, maximumFractionDigits: cents ? 2 : 0 })}`);

// "Ads → Revenue": spend (Meta Ads sync in PostHog) vs signups (PostHog
// first-touch UTM) vs revenue (Stripe), per campaign.
function AdRevenue({ days }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    axiosInstance
      .get(`/admin/analytics/funnel/ad-revenue?days=${days}`)
      .then((r) => setData(r.data))
      .catch((e) => setData({ error: e.response?.data?.error || e.message }));
  }, [days]);

  let body;
  if (!data) body = <div className="admindash-empty admindash-shimmer">Loading…</div>;
  else if (data.error) body = <div className="admindash-empty">Couldn't load: {data.error}</div>;
  else if (!data.posthogConfigured) {
    body = <div className="admindash-empty">Needs <b>POSTHOG_PERSONAL_API_KEY</b> on the API service in Render.</div>;
  } else {
    const t = data.totals;
    const roas = (v) => (v == null ? "—" : `${v.toFixed(2)}×`);
    body = (
      <>
        {!data.meta.connected && (
          <div className="funnel-errors">
            {data.meta.reason === "no_table"
              ? "Meta Ads spend isn't in PostHog yet — check Data pipelines → Sources → Meta Ads has finished syncing."
              : data.meta.reason === "columns"
              ? `Meta Ads table found but its columns weren't recognised: ${(data.meta.columns || []).join(", ")}`
              : `Couldn't read Meta Ads spend: ${data.meta.error || data.meta.reason}`}
          </div>
        )}
        {!data.stripe && <div className="funnel-errors">Stripe isn't reachable from the API — revenue shows $0.</div>}
        <div className="admindash-grid funnel-grid">
          <Stat label="Ad spend" value={money(t.spend)} sub={`${t.clicks.toLocaleString()} clicks`} />
          <Stat label="Signups from ads" value={t.signups.toLocaleString()} sub={`${money(t.costPerSignup, true)} each`} />
          <Stat label="Paying from ads" value={t.payers.toLocaleString()} sub={`${money(t.costPerPayer, true)} each`} />
          <Stat label="Revenue from ads" value={money(t.revenue)} sub={`ROAS ${roas(t.roas)}`} />
        </div>
        {data.campaigns.length === 0 ? (
          <div className="admindash-empty-sm">No campaigns or signups in this range yet.</div>
        ) : (
          <div className="funnel-table-scroll">
            <table className="admindash-table">
              <thead>
                <tr><th>Campaign</th><th>Spend</th><th>Signups</th><th>$/signup</th><th>Paying</th><th>$/payer</th><th>Revenue</th><th>ROAS</th></tr>
              </thead>
              <tbody>
                {data.campaigns.map((c) => (
                  <tr key={`${c.source}:${c.campaign}`}>
                    <td>
                      <span className="funnel-pill funnel-pill--idle">{SOURCE_LABEL[c.source] || c.source}</span>{" "}
                      <span className="funnel-ellipsis">{c.campaign}</span>
                    </td>
                    <td>{money(c.spend)}</td>
                    <td>{c.signups}</td>
                    <td>{money(c.costPerSignup, true)}</td>
                    <td><b>{c.payers}</b></td>
                    <td>{money(c.costPerPayer, true)}</td>
                    <td>{c.revenue ? money(c.revenue) : "—"}</td>
                    <td className={c.roas == null ? "muted" : c.roas >= 1 ? "funnel-good" : "funnel-bad"}>{roas(c.roas)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="funnel-note muted">
          Signups are new accounts in this range, grouped by the ad/UTM they first arrived from (or the Meta/TikTok click id when the ad has no UTM tags). Revenue is what those accounts paid in Stripe in the same range. Spend is Meta only (TikTok spend isn't synced).
          {data.diagnostics && (
            <> Traced {data.diagnostics.viaUtm + data.diagnostics.viaClickId} of {data.diagnostics.signups} signups
              ({data.diagnostics.viaUtm} by UTM, {data.diagnostics.viaClickId} by ad click id; PostHog matched {data.diagnostics.posthogMatched}).</>
          )}
        </div>
      </>
    );
  }

  return (
    <div className="admindash-section admindash-fade-in">
      <h2>Ads → Revenue</h2>
      {body}
    </div>
  );
}

export default function FunnelTab() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    axiosInstance
      .get(`/admin/analytics/funnel?days=${days}&tz=${encodeURIComponent(Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC")}`)
      .then((r) => setData(r.data))
      .catch((e) => {
        toast.error(e.response?.data?.error || "Failed to load funnel stats");
        setData({ error: true });
      });
  }, [days]);

  const rangePicker = (
    <div className="funnel-ranges">
      {RANGES.map((d) => (
        <button key={d} className={`admindash-tab ${days === d ? "active" : ""}`} onClick={() => setDays(d)}>
          {d}d
        </button>
      ))}
    </div>
  );

  if (!data) return <div className="admindash-empty admindash-shimmer">Loading…</div>;
  if (data.error) return <div className="admindash-empty">Couldn't load funnel stats.</div>;

  const { funnel, ads, daily, clicks, configured, trials, trialPipeline } = data;
  // Always show the rows that matter, even before any event has fired.
  const expected = [
    ["meta", "CompleteRegistration"], ["tiktok", "CompleteRegistration"],
    ["meta", "InitiateCheckout"], ["tiktok", "InitiateCheckout"],
    ["meta", "StartTrial"], ["tiktok", "StartTrial"],
    ["meta", "Purchase"], ["tiktok", "CompletePayment"],
  ];
  const adRows = expected.map(([platform, event]) =>
    ads.find((a) => a.platform === platform && a.event === event) || { platform, event, sent: 0, failed: 0, skipped: 0, value: 0, last: null }
  );
  const pct = (v) => (v == null ? "—" : `${v}%`);

  return (
    <div className="admindash-fade-in">
      <div className="admindash-section">
        <h2 className="admindash-section-title funnel-head">
          Funnel — last {days} days {rangePicker}
        </h2>
        <div className="admindash-grid funnel-grid">
          <Stat label="Get Clips clicks" value={funnel.getClipsClicks.toLocaleString()} />
          <Stat label="Signups" value={funnel.signups.toLocaleString()} sub={`${pct(funnel.clickToSignupPct)} of clicks`} />
          <Stat label="…of those now paying" value={funnel.signupsPaid.toLocaleString()} sub={`${pct(funnel.signupToPaidPct)} of signups`} />
          <Stat label="Paying subscribers (all time)" value={funnel.payingNow.toLocaleString()} />
        </div>
      </div>

      {trials && (
        <div className="admindash-section">
          <h2 className="admindash-section-title">$1 trial starts</h2>
          <div className="admindash-grid funnel-grid">
            <Stat label="Today" value={trials.today}
              sub={trials.today === trials.yesterday ? "same as yesterday" : `${trials.today > trials.yesterday ? "+" : ""}${trials.today - trials.yesterday} vs yesterday`} />
            <Stat label="Yesterday" value={trials.yesterday} />
            <Stat label="Last 7 days" value={trials.last7}
              sub={`${trials.last7 >= trials.prev7 ? "+" : ""}${trials.last7 - trials.prev7} vs the 7 days before`} />
            <Stat label={`Last ${days} days`} value={trials.inRange} />
          </div>
        </div>
      )}

      {trialPipeline && <TrialPipeline p={trialPipeline} />}

      <AdRevenue days={days} />

      <div className="admindash-section">
        <h2>Ad tracking — events sent to Meta & TikTok</h2>
        <table className="admindash-table">
          <thead>
            <tr><th>Platform</th><th>Event</th><th>Status</th><th>Sent</th><th>Failed</th><th>Not sent (no token)</th><th>Value</th><th>Last</th></tr>
          </thead>
          <tbody>
            {adRows.map((r) => {
              const h = health(r, configured[r.platform]);
              return (
                <tr key={`${r.platform}:${r.event}`} title={r.lastError ? `Last error: ${r.lastError}` : ""}>
                  <td>{PLATFORM_LABEL[r.platform]}</td>
                  <td>{EVENT_LABEL[r.event] || r.event}</td>
                  <td><span className={`funnel-pill funnel-pill--${h.tone}`}>{h.label}</span></td>
                  <td><b>{r.sent}</b></td>
                  <td>{r.failed || 0}</td>
                  <td>{r.skipped || 0}</td>
                  <td>{r.value ? `$${Math.round(r.value).toLocaleString()}` : "—"}</td>
                  <td className="muted">{rel(r.last)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {adRows.some((r) => r.lastError) && (
          <div className="funnel-errors">
            {adRows.filter((r) => r.lastError).map((r) => (
              <div key={`${r.platform}:${r.event}`}>
                <b>{PLATFORM_LABEL[r.platform]} {r.event}:</b> {r.lastError}
              </div>
            ))}
          </div>
        )}
        <TestEvent configured={configured} />
      </div>

      <div className="admindash-section admindash-fade-in">
        <h2>Day by day</h2>
        <div className="funnel-minis">
          <Bars title="Get Clips clicks" data={daily.getClipsClicks} />
          <Bars title="Signups sent to Meta" data={daily.metaRegistrations} />
          <Bars title="Signups sent to TikTok" data={daily.tiktokRegistrations} />
          <Bars title="Subscribers sent to Meta" data={daily.metaPurchases} orange />
          <Bars title="Subscribers sent to TikTok" data={daily.tiktokPurchases} orange />
          {trials && <Bars title="Trial starts (actual)" data={trials.daily} orange />}
          <Bars title="Trial starts sent to Meta" data={daily.metaTrials || []} orange />
          <Bars title="Trial starts sent to TikTok" data={daily.tiktokTrials || []} orange />
        </div>
        <div className="admindash-note">
          Each chart shows the last 7 days and the total for the range. Trial starts (actual) = $1 trials paid, by day in your
          time zone{trials ? ` (${trials.tz})` : ""}; the Meta and TikTok charts are what we reported to each ad platform (UTC days).
        </div>
      </div>

      <div className="admindash-section admindash-fade-in">
        <h2>Where Get Clips clicks come from</h2>
        {clicks.countries.length === 0 ? (
          <div className="admindash-empty-sm">No clicks recorded yet.</div>
        ) : (
          <div className="funnel-two-col">
            <table className="admindash-table">
              <thead><tr><th>Country</th><th>Clicks</th></tr></thead>
              <tbody>{clicks.countries.map((c) => <tr key={c.country}><td>{c.country}</td><td>{c.count}</td></tr>)}</tbody>
            </table>
            <table className="admindash-table">
              <thead><tr><th>City</th><th>Clicks</th></tr></thead>
              <tbody>
                {clicks.cities.map((c) => (
                  <tr key={`${c.city}-${c.region}-${c.country}`}>
                    <td>{c.city}{c.region ? `, ${c.region}` : ""} <span className="muted">{c.country}</span></td>
                    <td>{c.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="admindash-section admindash-fade-in">
        <h2>Most clicked buttons on clipbait.ai</h2>
        {clicks.buttons.length === 0 ? (
          <div className="admindash-empty-sm">No clicks recorded yet.</div>
        ) : (
          <table className="admindash-table">
            <thead><tr><th>Button</th><th>Page</th><th>Clicks</th></tr></thead>
            <tbody>
              {clicks.buttons.map((b) => (
                <tr key={`${b.label}|${b.page}`}>
                  <td>{/get clips/i.test(b.label) ? <b>{b.label}</b> : b.label}</td>
                  <td className="muted">{b.page}</td>
                  <td>{b.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <PostHogPanel ph={data.posthog} />
    </div>
  );
}
